import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { registerStudent, validateAssessment, getSessionStatus } from '../../api/publicAssessApi';
import CustomAlertModal from '../../components/common/CustomAlertModal';

const RegistrationPage = () => {
  const { register, handleSubmit, formState: { errors }, watch } = useForm();
  const [step, setStep] = useState(1);
  const [resumeFile, setResumeFile] = useState(null);
  const [facePhotoFile, setFacePhotoFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [assessmentInfo, setAssessmentInfo] = useState(null);
  const [validationError, setValidationError] = useState('');
  const navigate = useNavigate();
  const { token: urlToken } = useParams();
  const [alertConfig, setAlertConfig] = useState({ show: false, message: '', onConfirm: null });

  const showCustomAlert = (message, onConfirm = null) => {
    setAlertConfig({ show: true, message, onConfirm });
  };

  const hideCustomAlert = () => {
    const { onConfirm } = alertConfig;
    setAlertConfig({ show: false, message: '', onConfirm: null });
    if (onConfirm) onConfirm();
  };

  useEffect(() => {
    const checkToken = async () => {
      try {
        const res = await validateAssessment(urlToken);
        setAssessmentInfo(res.data);
      } catch (err) {
        setValidationError(err.response?.data?.message || 'Invalid or expired registration link.');
      } finally {
        setLoading(false);
      }
    };
    if (urlToken) {
      checkToken();
    }
  }, [urlToken]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        showCustomAlert('Only PDF files are allowed.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showCustomAlert('File size exceeds 5MB.');
        return;
      }
      setResumeFile(file);
    }
  };

  const watchedData = watch();

  const onSubmit = async (data) => {
    if (step < 3) {
      // On first step (email entered), check if student/session exists to decide flow
      if (step === 1 && data.email) {
        try {
          setLoading(true);
          const resp = await getSessionStatus(urlToken, data.email);
          // axiosClient already unwraps { success, data }, so resp.data may be the payload itself
          const sessionInfo = resp.data?.data ?? resp.data;
          const status = sessionInfo?.status;
          if (status === 'IN_PROGRESS') {
            const studentToken = sessionInfo?.studentToken;
            if (studentToken) {
              localStorage.setItem(`studentToken:${urlToken}`, studentToken);
              localStorage.setItem('studentToken', studentToken);
            }
            showCustomAlert('Welcome back. Resuming your in-progress assessment.', () => {
              navigate(`/assess/${urlToken}`);
            });
            return;
          }
          if (status === 'REGISTERED_NOT_STARTED') {
            const studentToken = resp.data?.data?.studentToken;
            if (studentToken) {
              localStorage.setItem(`studentToken:${urlToken}`, studentToken);
              localStorage.setItem('studentToken', studentToken);
            }
            navigate(`/assess/${urlToken}/instructions`);
            return;
          }
          if (status === 'ALREADY_REGISTERED') {
            setValidationError('ALREADY_REGISTERED');
            return;
          }
        } catch (err) {
          console.error('Session status check failed', err);
        } finally {
          setLoading(false);
        }
      }
      setStep(step + 1);
    } else {
      setIsSubmitting(true);
      setErrorMsg('');

      try {
        const formData = new FormData();
        Object.keys(data).forEach(key => {
          const value = data[key];
          if (value !== undefined && value !== null && value !== '') {
            formData.append(key, value === true ? 'true' : value === false ? 'false' : value);
          }
        });
        if (resumeFile) {
          formData.append('resume', resumeFile);
        }
        
        console.log('Face photo exists:', !!facePhotoFile);
        if (facePhotoFile) {
          formData.append('facePhoto', facePhotoFile);
        }

        console.log('--- FormData Entries ---');
        for (const pair of formData.entries()) {
            console.log(pair[0], pair[1] instanceof File ? `File: ${pair[1].name}` : pair[1]);
        }
        console.log('------------------------');

        const response = await registerStudent(urlToken, formData);
        const { studentToken, resumeAvailable } = response.data.data || response.data;
        if (studentToken) {
          localStorage.setItem(`studentToken:${urlToken}`, studentToken);
          localStorage.setItem('studentToken', studentToken);
        }
        
        if (resumeAvailable) {
          showCustomAlert('Welcome back. Resuming your in-progress assessment.', () => {
            navigate(`/assess/${urlToken}`);
          });
          return;
        }
        
        if (response.data?.data?.registeredNotStarted || response.data?.registeredNotStarted) {
          navigate(`/assess/${urlToken}/instructions`);
          return;
        }
        
        showCustomAlert('Registration Successful', () => {
          navigate(`/assess/${urlToken}/instructions`);
        });
      } catch (err) {
        console.error(err);
        setErrorMsg(err.response?.data?.message || 'Registration failed. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const StepIndicator = () => (
    <div className="d-flex justify-content-between mb-4 mb-md-5 position-relative">
      <div className="progress position-absolute top-50 start-0 translate-middle-y" style={{ width: '100%', height: '4px', zIndex: 0 }}>
        <div className="progress-bar bg-primary-custom" role="progressbar" style={{ width: `${((step - 1) / 2) * 100}%` }}></div>
      </div>
      {['Personal', 'Academic', 'Verify Details'].map((label, i) => {
        const s = i + 1;
        const isActive = step >= s;
        return (
          <div key={s} className="d-flex flex-column align-items-center position-relative z-1" style={{ width: '70px' }}>
            <div className={`rounded-circle d-flex align-items-center justify-content-center fw-bold mb-1 mb-md-2 ${isActive ? 'bg-primary-custom text-white' : 'bg-light text-muted border border-2'}`} style={{ width: '36px', height: '36px', fontSize: '14px' }}>
              {s}
            </div>
            <span className={`small fw-semibold text-center ${isActive ? 'text-primary-custom' : 'text-muted'}`} style={{ fontSize: '12px' }}>{label}</span>
          </div>
        );
      })}
    </div>
  );

  if (loading) {
    return <div className="vh-100 d-flex justify-content-center align-items-center"><div className="spinner-border text-primary-custom"></div></div>;
  }

  if (validationError === 'ALREADY_REGISTERED') {
    return (
      <div className="container py-4 py-md-5 text-center px-3">
        <h3 className="text-success fw-bold">You are already registered.</h3>
        <button 
          className="btn btn-primary-custom mt-4 px-4 py-2 rounded-pill fw-bold"
          onClick={() => navigate(`/assess/${urlToken}`)}
        >
          Continue to Assessment
        </button>
      </div>
    );
  }

  if (validationError) {
    return (
      <div className="container py-4 py-md-5 text-center px-3">
        <h3 className="text-danger fw-bold">{validationError === 'Registration is closed for this assessment' ? 'Registration Closed' : 'Access Denied'}</h3>
        <p className="text-muted fs-5">{validationError === 'Registration is closed for this assessment' ? 'Registration is no longer accepting new candidates.' : validationError}</p>
      </div>
    );
  }

  return (
    <div className="container py-3 py-md-5 px-2 px-md-3">
      <div className="card custom-card border-0 shadow-lg mx-auto" style={{ maxWidth: '800px', borderRadius: '16px' }}>
        <div className="card-body p-3 p-md-5">
          <div className="text-center mb-4 mb-md-5">
            <h2 className="text-primary-custom fw-bold fs-3 fs-md-2">Candidate Registration</h2>
            <p className="text-muted">{assessmentInfo?.name || 'Assessment Drive'}</p>
          </div>

          <StepIndicator />
          
          {errorMsg && <div className="alert alert-danger py-2">{errorMsg}</div>}

          <form onSubmit={handleSubmit(onSubmit)}>
            
            {step === 1 && (
              <div className="row g-3 fade show">
                <h5 className="mb-3 border-bottom pb-2 text-primary-custom">Personal Details</h5>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">First Name <span className="text-danger">*</span></label>
                  <input type="text" className={`form-control p-2 ${errors.firstName ? 'is-invalid' : ''}`} {...register('firstName', { required: true })} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Last Name <span className="text-danger">*</span></label>
                  <input type="text" className={`form-control p-2 ${errors.lastName ? 'is-invalid' : ''}`} {...register('lastName', { required: true })} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Email <span className="text-danger">*</span></label>
                  <input type="email" className={`form-control p-2 ${errors.email ? 'is-invalid' : ''}`} {...register('email', { required: true, pattern: /^\S+@\S+$/i })} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Mobile Number <span className="text-danger">*</span></label>
                  <input type="tel" className={`form-control p-2 ${errors.mobileNumber ? 'is-invalid' : ''}`} {...register('mobileNumber', { required: true })} />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="row g-3 fade show">
                <h5 className="mb-3 border-bottom pb-2 text-primary-custom">Academic Details</h5>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">College Name <span className="text-danger">*</span></label>
                  <input type="text" className={`form-control p-2 ${errors.collegeName ? 'is-invalid' : ''}`} {...register('collegeName', { required: true })} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Degree <span className="text-danger">*</span></label>
                  <select className={`form-select p-2 ${errors.degree ? 'is-invalid' : ''}`} {...register('degree', { required: true })}>
                    <option value="">Select Degree</option>
                    <option value="BTech">B.Tech / B.E.</option>
                    <option value="MTech">M.Tech / M.E.</option>
                    <option value="MCA">MCA</option>
                    <option value="BCA">BCA</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Branch <span className="text-danger">*</span></label>
                  <input type="text" className={`form-control p-2 ${errors.branch ? 'is-invalid' : ''}`} {...register('branch', { required: true })} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Passing Year <span className="text-danger">*</span></label>
                  <input type="text" inputMode="numeric" maxLength={4} placeholder="e.g. 2026" className={`form-control p-2 ${errors.passingYear ? 'is-invalid' : ''}`} {...register('passingYear', { required: true, pattern: /^[0-9]{4}$/ })} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">GPA / CGPA <span className="text-danger">*</span></label>
                  <input type="text" className={`form-control p-2 ${errors.gpa ? 'is-invalid' : ''}`} {...register('gpa', { required: true })} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Roll Number <span className="text-danger">*</span></label>
                  <input type="text" className={`form-control p-2 ${errors.rollNumber ? 'is-invalid' : ''}`} {...register('rollNumber', { required: true })} />
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="row g-3 fade show">
                <div className="col-12">
                  <div className="card border-0 shadow-sm rounded-4">
                    <div className="card-body p-4">
                      <h5 className="mb-4 border-bottom pb-2 text-primary-custom">Verify Details & Continue</h5>

                      <div className="mb-4">
                        <h6 className="fw-bold mb-3">Personal Details</h6>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="form-control-plaintext mb-2">
                              <span className="text-muted d-block">First Name</span>
                              <span className="fw-semibold">{watchedData.firstName || ''}</span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-control-plaintext mb-2">
                              <span className="text-muted d-block">Last Name</span>
                              <span className="fw-semibold">{watchedData.lastName || ''}</span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-control-plaintext mb-2">
                              <span className="text-muted d-block">Email</span>
                              <span className="fw-semibold">{watchedData.email || ''}</span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-control-plaintext mb-2">
                              <span className="text-muted d-block">Mobile Number</span>
                              <span className="fw-semibold">{watchedData.mobileNumber || ''}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h6 className="fw-bold mb-3">Academic Details</h6>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="form-control-plaintext mb-2">
                              <span className="text-muted d-block">College Name</span>
                              <span className="fw-semibold">{watchedData.collegeName || ''}</span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-control-plaintext mb-2">
                              <span className="text-muted d-block">Degree</span>
                              <span className="fw-semibold">{watchedData.degree || ''}</span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-control-plaintext mb-2">
                              <span className="text-muted d-block">Branch</span>
                              <span className="fw-semibold">{watchedData.branch || ''}</span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-control-plaintext mb-2">
                              <span className="text-muted d-block">Passing Year</span>
                              <span className="fw-semibold">{watchedData.passingYear || ''}</span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-control-plaintext mb-2">
                              <span className="text-muted d-block">GPA / CGPA</span>
                              <span className="fw-semibold">{watchedData.gpa || ''}</span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-control-plaintext mb-2">
                              <span className="text-muted d-block">Roll Number</span>
                              <span className="fw-semibold">{watchedData.rollNumber || ''}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="form-check mt-4">
                        <input
                          className={`form-check-input fs-5 ${errors.declarationAccepted ? 'is-invalid' : ''}`}
                          type="checkbox"
                          id="declarationAccepted"
                          {...register('declarationAccepted', { required: true })}
                        />
                        <label className="form-check-label ms-2 mt-1 fw-semibold text-dark" htmlFor="declarationAccepted">
                          I verify that all the above information is correct.
                        </label>
                      </div>
                      {errors.declarationAccepted && (
                        <div className="invalid-feedback d-block">You must verify your details before continuing.</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="d-flex flex-wrap gap-2 justify-content-between mt-4 mt-md-5 pt-3 pt-md-4 border-top">
              {step > 1 ? (
                <button type="button" className="btn btn-outline-secondary px-3 px-md-4 rounded-pill fw-semibold" onClick={() => setStep(step - 1)}>Go Back</button>
              ) : (
                <button type="button" className="btn btn-light px-3 px-md-4 rounded-pill fw-semibold text-muted">Clear Form</button>
              )}
              
              <button 
                type="submit" 
                className="btn btn-primary-custom px-4 px-md-5 fw-bold rounded-pill shadow-sm d-flex align-items-center gap-2"
                disabled={isSubmitting}
              >
                {isSubmitting && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
                {step === 3 ? (isSubmitting ? 'Submitting...' : 'Complete Registration') : 'Save & Continue'}
              </button>
            </div>
          </form>

        </div>
      </div>
      <CustomAlertModal show={alertConfig.show} message={alertConfig.message} onHide={hideCustomAlert} />
    </div>
  );
};

export default RegistrationPage;
